// =============================================================================
// CORE CONFIGURATION OBJECTS
// =============================================================================

import { UI_CONSTANTS } from '../constants';
import { getSourceIconByName } from './iconHelpers';
import { storageService } from '../services/storageService';

// =============================================================================
// CONFIGURATION HELPER FUNCTIONS - Read from storageService
// =============================================================================

const getEntityConfig = () => {
  return storageService.getEntityConfiguration();
};

const getGroupableFields = () => {
  const config = getEntityConfig();
  return config?.groupableFields || [];
};

const getNestedArrayFields = () => {
  const config = getEntityConfig();
  return config?.nestedArrayFields || [];
};

const getMaxValueFields = () => {
  const config = getEntityConfig();
  return config?.maxValueFields || [];
};

/**
 * Get field labels from configuration
 * @returns {Object} Object mapping field keys to their labels
 */
export const getFieldLabels = () => {
  const config = getEntityConfig();
  return config?.fieldLabels || {};
};

const getDateFields = () => {
  const config = getEntityConfig();
  return config?.dateFields || [];
};

const getTextFields = () => {
  const config = getEntityConfig();
  return config?.textFields || [];
};

const fieldTypeConfig = {
  get groupable() {
    return getGroupableFields();
  },
  get text() {
    return getTextFields();
  },
  object: [],
  get date() {
    return getDateFields();
  },
  get number() {
    return getMaxValueFields();
  }
};

// =============================================================================
// FIELD TYPE DETECTION AND UTILITY FUNCTIONS
// =============================================================================

export const getFieldType = (fieldKey) => {
  for (const [type, fields] of Object.entries(fieldTypeConfig)) {
    if (fields.includes(fieldKey)) {
      return type;
    }
    const lowerFieldKey = fieldKey.toLowerCase();
    const matchesIgnoreCase = fields.some(configField =>
      configField.toLowerCase() === lowerFieldKey ||
      configField.toLowerCase().replace(/[_-]/g, '') === lowerFieldKey.replace(/[_-]/g, '')
    );
    if (matchesIgnoreCase) {
      return type;
    }
  }
  return 'text';
};

export const isGroupableField = (fieldKey) => {
  const type = getFieldType(fieldKey);
  return type === 'groupable';
};

const isDateField = (fieldKey) => {
  return getFieldType(fieldKey) === 'date';
};

export const isDateFieldEnhanced = (fieldKey, fieldLabel = '') => {
  if (isDateField(fieldKey)) {
    return true;
  }
  const datePattern = /date|time|created|published|updated|modified/i;
  return datePattern.test(fieldKey) || datePattern.test(fieldLabel);
};

export const getBadgeStyles = (backgroundColor, options = {}) => {
  const { isCompact = false, ...customStyles } = options;

  return {
    ...UI_CONSTANTS.BADGE_STYLES,
    background: backgroundColor,
    minWidth: isCompact ? UI_CONSTANTS.BADGE_MIN_WIDTH.COMPACT : UI_CONSTANTS.BADGE_MIN_WIDTH.STANDARD,
    ...customStyles
  };
};

// =============================================================================
// SPECIAL FIELD CONFIGURATIONS
// =============================================================================

const numberFieldConfigs = {
  totalLikesCount: {
    min: 0,
    max: 100000,
    step: 1,
    formatValue: (val) => val.toString()
  },
  totalCommentsCount: {
    min: 0,
    max: 100000,
    step: 1,
    formatValue: (val) => val.toString()
  },
  totalSharesCount: {
    min: 0,
    max: 100000,
    step: 1,
    formatValue: (val) => val.toString()
  },
  totalRetweetCount: {
    min: 0,
    max: 100000,
    step: 1,
    formatValue: (val) => val.toString()
  },
  totalViewsCount: {
    min: 0,
    max: 100000,
    step: 1,
    formatValue: (val) => val.toString()
  },
  totalBookmarksCount: {
    min: 0,
    max: 100000,
    step: 1,
    formatValue: (val) => val.toString()
  }
};

export const getNumberFieldConfig = (fieldKey) => {
  return numberFieldConfigs[fieldKey] || {
    min: 0,
    max: 1000,
    step: 1,
    formatValue: (val) => val.toString()
  };
};

export const getSourceIcon = (value) => {
  return getSourceIconByName(value);
};

/**
 * Converts raw post-type values like "BACKGROUND_SONG - Instagram"
 * into friendly display labels like "Background Song".
 * Strips the " - Platform" suffix and title-cases the remainder.
 */
export const formatPostTypeLabel = (raw) => {
  if (!raw || typeof raw !== 'string') return raw;
  const withoutPlatform = raw.replace(/\s*-\s*[^-]+$/, '');
  return withoutPlatform
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());
};

// =============================================================================
// HELPER AND UTILITY FUNCTIONS
// =============================================================================

const getNestedGroupingFields = () => {
  return getNestedArrayFields();
};

export const isNestedGroupingField = (fieldKey) => {
  const nestedGroupingFields = getNestedGroupingFields();
  return nestedGroupingFields.some(field =>
    field === fieldKey ||
    field.toLowerCase() === fieldKey.toLowerCase() ||
    field.toLowerCase().replace(/[_-]/g, '') === fieldKey.toLowerCase().replace(/[_-]/g, '')
  );
};

function parseArrayValue(value) {
  if (!value) return [];

  let items = [];

  if (Array.isArray(value)) {
    items = value.filter(item => item && item.toString().trim());
  } else if (typeof value === 'string') {
    if (value.startsWith('[') && value.endsWith(']')) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          items = parsed.filter(item => item && item.toString().trim());
        }
      } catch (e) {
        if (value.trim() && value !== '[]') {
          items = [value];
        }
      }
    } else if (value.startsWith('{') && value.endsWith('}')) {
      const content = value.slice(1, -1);
      if (content.trim()) {
        const matches = content.match(/("(?:[^"\\]|\\.)*"|[^,]+)/g) || [];
        items = matches
          .map(item => item.trim())
          .filter(item => item)
          .map(item => {
            if (item.startsWith('"') && item.endsWith('"')) {
              return item.slice(1, -1);
            }
            return item;
          });
      }
    } else if (value.includes(',') && !value.includes('"')) {
      items = value.split(',').map(item => item.trim()).filter(item => item);
    } else if (value.trim() && value !== '[]' && value !== '{}') {
      items = [value.trim()];
    }
  }

  return items;
}

export const processNestedGrouping = (filterOptions, fieldKey) => {
  if (!isNestedGroupingField(fieldKey) || !Array.isArray(filterOptions)) {
    return filterOptions;
  }

  const flattenedOptions = new Set();

  filterOptions.forEach(option => {
    if (option === null || option === undefined) {
      flattenedOptions.add(null);
      return;
    }

    let valueToProcess = option;

    if (typeof option === 'object' && option !== null && 'value' in option) {
      valueToProcess = option.value;
    }

    const items = parseArrayValue(valueToProcess);

    items.forEach(item => {
      if (item && item.toString().trim()) {
        flattenedOptions.add(item.toString().trim());
      }
    });
  });

  const result = Array.from(flattenedOptions)
    .filter(item => item !== null)
    .sort()
    .map(item => ({
      value: item,
      label: item,
      color: UI_CONSTANTS.DEFAULT_COLOR
    }));

  return result;
};
